import { useState, useMemo } from 'react'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import { DataTableHeader } from './DataTableHeader'
import { DataTableBody } from './DataTableBody'
import { DataTableColumnSettings } from './DataTableColumnSettings'
import { useDataTableScroll } from './hooks/useDataTableScroll'
import { useDataTableResize } from './hooks/useDataTableResize'
import type { DataTableProps, DataTableItem, EditingCell } from './types'

function parseWidth(width: string): number {
  const match = width.match(/^(\d+(?:\.\d+)?)(px|%)?$/)
  return match ? parseFloat(match[1]) : 100
}

export function DataTable<T extends DataTableItem>({
  data,
  columns,
  isLoading = false,
  selection,
  rowActions,
  columnVisibility,
  emptyState,
  skeletonRowCount = 20,
  resizable = false,
  resizeConfig,
  onCellEdit,
  showPlaceholderWhenEmpty = false,
  disableRowHover: disableRowHoverProp,
  sort,
  onSortChange
}: DataTableProps<T>) {
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false)
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null)
  const { isScrolledX, handleScroll } = useDataTableScroll()

  const visibleColumns = columnVisibility?.visibleColumns || columns.map(c => c.id)
  const hasActions = rowActions && rowActions.length > 0
  const hasEditableCells = columns.some(col => col.editable) || !!onCellEdit
  const disableRowHover = disableRowHoverProp ?? hasEditableCells

  const {
    columnWidths,
    resizingColumnId,
    onResizeStart,
    getColumnWidth
  } = useDataTableResize(columns, visibleColumns, resizeConfig)

  // Calculate column widths and total table width
  const { columnWidthsList, totalTableWidth } = useMemo(() => {
    const visibleCols = columns.filter(col => visibleColumns.includes(col.id))
    const widths = visibleCols.map(col => {
      if (resizable) {
        return getColumnWidth(col.id)
      }
      return parseWidth(col.width)
    })
    const actionsWidth = (hasActions || columnVisibility) ? 200 : 0
    const total = widths.reduce((sum, w) => sum + w, 0) + actionsWidth
    return { columnWidthsList: widths, totalTableWidth: total }
  }, [columns, visibleColumns, resizable, getColumnWidth, hasActions, columnVisibility])

  const handleStartEdit = (cell: EditingCell) => {
    setEditingCell(cell)
  }

  const handleCancelEdit = () => {
    setEditingCell(null)
  }

  const handleSaveEdit = async (rowId: string | number, columnId: string, value: string) => {
    setEditingCell(null)
    if (onCellEdit) {
      await onCellEdit(rowId, columnId, value)
    }
  }

  return (
    <div className="relative h-full">
      <OverlayScrollbarsComponent
          className="h-full"
          options={{
            scrollbars: {
              autoHide: 'never',
              theme: 'os-theme-dark'
            }
          }}
          events={{
            scroll: handleScroll
          }}
          style={{ contain: 'strict' }}
        >
          <div className="relative" data-resize-container>
          {/* No border of its own: every row draws its bottom rule, and an
              empty table would otherwise double the header's. */}
          <table
            style={{
              tableLayout: 'fixed',
              width: '100%',
              minWidth: resizable ? `${totalTableWidth}px` : undefined
            }}
          >
          <colgroup>
            {columns
              .filter(col => visibleColumns.includes(col.id))
              .map((col, index) => (
                <col
                  key={col.id}
                  style={{ width: `${columnWidthsList[index]}px` }}
                />
              ))}
            {(hasActions || columnVisibility) && (
              <col style={{ width: '13.33rem' }} />
            )}
            {/* Spacer column to absorb extra space without affecting other columns */}
            {resizable && <col style={{ width: 'auto' }} />}
          </colgroup>

          <DataTableHeader
            columns={columns}
            visibleColumns={visibleColumns}
            selection={selection}
            dataLength={data.length}
            isScrolledX={isScrolledX}
            onColumnSettingsClick={columnVisibility ? () => setColumnSettingsOpen(true) : undefined}
            hasActions={hasActions}
            resizable={resizable}
            columnWidths={columnWidths}
            resizingColumnId={resizingColumnId}
            onResizeStart={onResizeStart}
            hasSpacerColumn={resizable}
            sort={sort}
            onSortChange={onSortChange}
          />

          <DataTableBody
            data={data}
            columns={columns}
            visibleColumns={visibleColumns}
            isLoading={isLoading}
            selection={selection}
            rowActions={rowActions}
            isScrolledX={isScrolledX}
            skeletonRowCount={skeletonRowCount}
            editingCell={editingCell}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            showPlaceholderWhenEmpty={showPlaceholderWhenEmpty}
            disableRowHover={disableRowHover}
            hasSpacerColumn={resizable}
          />
          </table>
          </div>
      </OverlayScrollbarsComponent>

      {!isLoading && data.length === 0 && emptyState && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {emptyState}
        </div>
      )}

      {columnVisibility && (
        <DataTableColumnSettings
          open={columnSettingsOpen}
          onOpenChange={setColumnSettingsOpen}
          columns={columns}
          visibleColumns={visibleColumns}
          onSave={columnVisibility.onSave}
        />
      )}
    </div>
  )
}
